import React, { useState, useEffect } from 'react';
import { ScrollView } from 'react-native';
import { useSelector } from 'react-redux';
import { useLinkTo } from '@react-navigation/native';
import Typography from 'src/components/Text/Text';
import IconInput from 'src/components/IconInput/IconInput';
import RadioModal from 'src/components/RadioModal/RadioModal';
import NavigationButtons from '../../components/NavigationButtons';
import EldSection, { eldVendorValueMap, eldDataSharingValueMap } from '../../components/EldSection';
import Toast from 'src/components/Toast/Toast';
import { useUpdateApplicationMutation } from '../../api/applicationApi';
import { RootState } from 'src/store/store';
import { URLS } from 'src/constants/urls';
import { toggleToastState } from 'src/helpers/toastHelper';
import arrowDown from 'src/assets/arrowDown-3x.png';
import { styles } from '../Policy/PolicyScreen.styles';

const insuranceHistoryOptions = [
  { label: 'Unknown' },
  { label: 'Continuous (no gaps)' },
  { label: 'Has gap(s)' },
];

const yesNoOptions = [{ label: 'Yes' }, { label: 'No' }];

const boolToYesNo = (val?: boolean | null) => (val === true ? 'Yes' : val === false ? 'No' : '');
const yesNoToBool = (val: string): boolean | null =>
  val === 'Yes' ? true : val === 'No' ? false : null;

const OthersScreen: React.FC = () => {
  const linkTo = useLinkTo();
  const application = useSelector((state: RootState) => state.application.application);
  const currentCompany = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);
  const isProducer = accountType === 'producer';

  const [insuranceHistory, setInsuranceHistory] = useState(application?.insuranceHistory || '');
  const [priorInsuranceCancelled, setPriorInsuranceCancelled] = useState(
    boolToYesNo(application?.priorInsuranceCancelled),
  );
  const [bankruptciesLastFiveYears, setBankruptciesLastFiveYears] = useState(
    boolToYesNo(application?.bankruptciesLastFiveYears),
  );
  const [peronsalUseOfVehicle, setPeronsalUseOfVehicle] = useState(
    boolToYesNo(application?.peronsalUseOfVehicle),
  );
  const [eldVendor, setEldVendor] = useState(application?.eldVendor || '');
  const [eldDataSharingOption, setEldDataSharingOption] = useState(
    application?.eldDataSharingOption || '',
  );

  const [isInsuranceHistoryModalVisible, setInsuranceHistoryModalVisible] = useState(false);
  const [isPriorInsuranceModalVisible, setPriorInsuranceModalVisible] = useState(false);
  const [isBankruptciesModalVisible, setBankruptciesModalVisible] = useState(false);
  const [isPersonalUseModalVisible, setPersonalUseModalVisible] = useState(false);
  const [saveErrorToast, setSaveErrorToast] = useState(false);

  const [updateApplication] = useUpdateApplicationMutation();

  useEffect(() => {
    if (!isProducer) {
      linkTo(URLS.APPLICATION.LOSS);
    }
  }, [isProducer, linkTo]);

  if (!isProducer) return null;

  const isNextDisabled = false;

  const handleNext = async () => {
    try {
      await updateApplication({
        id: application?.id as number,
        updatedData: {
          insuranceHistory,
          priorInsuranceCancelled: yesNoToBool(priorInsuranceCancelled),
          bankruptciesLastFiveYears: yesNoToBool(bankruptciesLastFiveYears),
          peronsalUseOfVehicle: yesNoToBool(peronsalUseOfVehicle),
          ...(isProducer && {
            eldVendor: eldVendorValueMap[eldVendor] || eldVendor,
            eldDataSharingOption:
              eldDataSharingValueMap[eldDataSharingOption] || eldDataSharingOption,
          }),
          company: { id: currentCompany?.id || 0 },
        },
      }).unwrap();
      linkTo(isProducer ? URLS.APPLICATION.GROWTH_AND_OWNERSHIP : URLS.APPLICATION.LOSS);
    } catch {
      setSaveErrorToast(true);
      toggleToastState(setSaveErrorToast);
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
      <Typography variant="h2">Risk History</Typography>

      <IconInput
        label="Insurance History"
        testID="others-insurance-history"
        value={insuranceHistory}
        placeholder="Select insurance history"
        onPress={() => setInsuranceHistoryModalVisible(true)}
        iconSource={arrowDown}
        iconHeight={6.67}
        iconWidth={16.67}
      />
      <RadioModal
        testID="others-insurance-history-modal"
        isVisible={isInsuranceHistoryModalVisible}
        onSelectOption={setInsuranceHistory}
        onButtonClickLeft={() => setInsuranceHistoryModalVisible(false)}
        onClose={() => setInsuranceHistoryModalVisible(false)}
        title="Select insurance history"
        options={insuranceHistoryOptions}
        selectedValue={insuranceHistory}
      />

      <IconInput
        label="Has prior insurance been cancelled in the last 5 years?"
        testID="others-prior-insurance"
        value={priorInsuranceCancelled}
        placeholder="Select an option"
        onPress={() => setPriorInsuranceModalVisible(true)}
        iconSource={arrowDown}
        iconHeight={6.67}
        iconWidth={16.67}
      />
      <RadioModal
        testID="others-prior-insurance-modal"
        isVisible={isPriorInsuranceModalVisible}
        onSelectOption={setPriorInsuranceCancelled}
        onButtonClickLeft={() => setPriorInsuranceModalVisible(false)}
        onClose={() => setPriorInsuranceModalVisible(false)}
        title="Has prior insurance been cancelled in the last 5 years?"
        options={yesNoOptions}
        selectedValue={priorInsuranceCancelled}
      />

      <IconInput
        label="Have you had bankruptcies in the last 5 years?"
        testID="others-bankruptcies"
        value={bankruptciesLastFiveYears}
        placeholder="Select an option"
        onPress={() => setBankruptciesModalVisible(true)}
        iconSource={arrowDown}
        iconHeight={6.67}
        iconWidth={16.67}
      />
      <RadioModal
        testID="others-bankruptcies-modal"
        isVisible={isBankruptciesModalVisible}
        onSelectOption={setBankruptciesLastFiveYears}
        onButtonClickLeft={() => setBankruptciesModalVisible(false)}
        onClose={() => setBankruptciesModalVisible(false)}
        title="Have you had bankruptcies in the last 5 years?"
        options={yesNoOptions}
        selectedValue={bankruptciesLastFiveYears}
      />

      <IconInput
        label="Is the vehicle used for personal purposes?"
        testID="others-personal-use"
        value={peronsalUseOfVehicle}
        placeholder="Select an option"
        onPress={() => setPersonalUseModalVisible(true)}
        iconSource={arrowDown}
        iconHeight={6.67}
        iconWidth={16.67}
      />
      <RadioModal
        testID="others-personal-use-modal"
        isVisible={isPersonalUseModalVisible}
        onSelectOption={setPeronsalUseOfVehicle}
        onButtonClickLeft={() => setPersonalUseModalVisible(false)}
        onClose={() => setPersonalUseModalVisible(false)}
        title="Is the vehicle used for personal purposes?"
        options={yesNoOptions}
        selectedValue={peronsalUseOfVehicle}
      />

      {isProducer && (
        <EldSection
          eld={{
            vendor: eldVendor,
            setVendor: setEldVendor,
            dataSharingOption: eldDataSharingOption,
            setDataSharingOption: setEldDataSharingOption,
          }}
        />
      )}

      {saveErrorToast && (
        <Toast
          text="An error occurred while saving. Please try again."
          type="error"
          animate={true}
        />
      )}

      <NavigationButtons
        testID="others-nav-btn"
        onBack={() => linkTo(URLS.APPLICATION.POLICY)}
        onNext={handleNext}
        isNextDisabled={isNextDisabled}
      />
    </ScrollView>
  );
};

export default OthersScreen;
